import { useState } from "react";
import { Info, AlertTriangle } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import SitemateFooter from "@/components/SitemateFooter";
import type { GeneratorConfig } from "@/types/generator";

interface AboutModalProps {
  config: GeneratorConfig;
  variant?: "default" | "white";
}

export function AboutModal({ config, variant = "default" }: AboutModalProps) {
  const [open, setOpen] = useState(false);

  // Don't render if no about content is available
  if (!config.about_what_is && !config.about_how_it_works) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          className={cn(
            "flex items-center gap-2 rounded-full px-4 h-9 text-sm font-medium border border-border/50 shadow-sm transition-colors",
            variant === "white"
              ? "bg-card hover:bg-muted text-foreground"
              : "bg-muted/50 hover:bg-muted text-foreground"
          )}
        >
          <Info className="w-3.5 h-3.5" />
          <span>About</span>
        </button>
      </DialogTrigger>
       <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden" style={{ display: 'flex', flexDirection: 'column' }}>
        <DialogHeader className="shrink-0">
          <DialogTitle>About {config.display_name}</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="space-y-5 text-sm leading-relaxed text-foreground pr-4">
          {config.about_what_is && (
            <section>
              <h3 className="font-semibold text-base mb-2">
                What is the {config.display_name}?
              </h3>
              <p className="text-muted-foreground">{config.about_what_is}</p>
            </section>
          )}

          {config.about_how_it_works && (
            <section>
              <h3 className="font-semibold text-base mb-2">
                How does the {config.display_name} work?
              </h3>
              <p className="text-muted-foreground">{config.about_how_it_works}</p>
            </section>
          )}

          {config.disclaimer && (
            <div className="flex gap-2.5 rounded-lg border border-border bg-muted/50 p-3">
              <AlertTriangle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                {config.disclaimer}
              </p>
            </div>
          )}
          </div>
        </div>

        <div className="shrink-0 pt-4 border-t border-border">
          <SitemateFooter fixed={false} generatorSlug={config.slug} placement="about" />
        </div>
      </DialogContent>
    </Dialog>
  );
}
